/**
 * `/admin/analysis` — ambient-analysis calibration surface (phase 4).
 *
 * The review queue for machine-produced findings: proof-score-ordered
 * list, per-finding evidence + score breakdown, and verdict capture.
 * `good/(good+bad)` at the top is the feature's go/no-go number.
 *
 * Verdict vocabulary: good / bad / unsure / not useful. "Not useful"
 * exists so "true but nobody would care" is measurable without
 * polluting precision — it signals the PRIORITY formula needs tuning,
 * not the detector. Gated by the parent `_app/admin` AdminGate.
 */
import { Island } from '@/components/shell/Island';
import {
  type AdminAnalysisFinding,
  type AdminAnalysisTick,
  useAdminAnalysisFinding,
  useAdminAnalysisFindings,
  useAdminAnalysisObservations,
  useAdminAnalysisStats,
  useAnalysisVerdictMutation,
  useIsSystemAdmin,
  useReleaseFindingMutation,
  useRunAnalysisMutation,
} from '@/lib/queries/admin';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/admin/analysis')({
  component: AdminAnalysisPage,
});

const KINDS = ['', 'deploy_impact', 'third_party_impact', 'cohort_divergence', 'sustained_shift'];
const VERDICTS: { value: string; label: string }[] = [
  { value: 'good', label: 'Good' },
  { value: 'bad', label: 'Bad' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'not_useful', label: 'Not useful' },
];

function fmtDay(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function StatCard({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function DirectionBadge({ direction }: { direction: string }): ReactElement {
  const positive = direction === 'improvement';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs ${
        positive
          ? 'bg-[var(--color-success-soft,rgba(14,159,110,0.12))] text-[var(--color-success,#0e9f6e)]'
          : 'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]'
      }`}
    >
      {direction}
    </span>
  );
}

/** Latest verdict per reviewer collapses to one chip; empty = unreviewed. */
function VerdictChips({ finding }: { finding: AdminAnalysisFinding }): ReactElement {
  if (finding.verdicts.length === 0) {
    return <span className="text-xs text-[var(--color-mute)]">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {finding.verdicts.map((v) => (
        <span
          key={v.reviewed_by_clerk_user_id}
          title={v.note ?? undefined}
          className="rounded bg-[var(--color-paper-alt)] px-1.5 py-0.5 text-xs"
        >
          {v.verdict.replace('_', ' ')}
        </span>
      ))}
    </span>
  );
}

function ScoreBreakdown({ components }: { components: Record<string, number> }): ReactElement {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(components).map(([name, points]) => (
        <span
          key={name}
          className="rounded border border-[var(--color-line)] px-2 py-0.5 text-xs tabular-nums"
        >
          {name.replace(/_/g, ' ')}{' '}
          <strong className={points < 0 ? 'text-[var(--color-danger,#dc2626)]' : ''}>
            {points > 0 ? `+${points}` : points}
          </strong>
        </span>
      ))}
    </div>
  );
}

function FindingDetail({ uuid }: { uuid: string }): ReactElement {
  const detail = useAdminAnalysisFinding(uuid);
  const verdictMutation = useAnalysisVerdictMutation();
  const releaseMutation = useReleaseFindingMutation();
  const [note, setNote] = useState('');

  if (detail.isLoading) {
    return <div className="p-4 text-sm text-[var(--color-mute)]">Loading evidence…</div>;
  }
  if (!detail.data) {
    return <div className="p-4 text-sm text-[var(--color-mute)]">Couldn't load this finding.</div>;
  }
  const { finding, series } = detail.data;
  const ev = finding.evidence;

  return (
    <div className="space-y-4 border-t border-[var(--color-line)] bg-[var(--color-paper-alt)] p-4">
      {ev.score_components && <ScoreBreakdown components={ev.score_components} />}

      {/* Evidence lines — mirrors the email's "what we looked at". */}
      <div className="space-y-1 text-sm">
        {ev.persistence_days !== undefined && (
          <div>Held {ev.persistence_days} consecutive day(s).</div>
        )}
        {(ev.candidates ?? []).map((cand, i) => (
          <div key={`${cand.type}-${i}`}>
            Candidate: {cand.type}
            {'entity' in cand ? ` — ${String(cand.entity)}` : ''}
            {'deploy_id' in cand ? ` — ${String(cand.deploy_id)}` : ''}
            {'alignment_hours' in cand
              ? ` (${Number(cand.alignment_hours).toFixed(1)}h from onset)`
              : ''}
          </div>
        ))}
        {(ev.corroboration ?? []).map((c) => (
          <div key={c.source}>
            Corroboration: {c.source} {c.agrees ? 'agrees' : 'disagrees'} — {c.detail}
          </div>
        ))}
        {ev.impact && (
          <div>
            Reach: {(ev.impact.share_of_traffic * 100).toFixed(0)}% of traffic,{' '}
            {ev.impact.sessions_affected} samples.
          </div>
        )}
      </div>

      {/* The observation series: value vs baseline, deviating days emphasised. */}
      {series.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs tabular-nums">
            <tbody>
              <tr className="text-[var(--color-mute)]">
                <td className="pr-3 py-0.5">day</td>
                {series.map((p) => (
                  <td key={p.observed_on} className="px-2 text-center">
                    {fmtDay(p.observed_on)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="pr-3 py-0.5 text-[var(--color-mute)]">value</td>
                {series.map((p) => (
                  <td
                    key={p.observed_on}
                    className={`px-2 text-center ${
                      Math.abs(p.deviation) >= 3 ? 'font-semibold' : ''
                    }`}
                  >
                    {Math.round(p.value * 100) / 100}
                  </td>
                ))}
              </tr>
              <tr className="text-[var(--color-mute)]">
                <td className="pr-3 py-0.5">baseline</td>
                {series.map((p) => (
                  <td key={p.observed_on} className="px-2 text-center">
                    {Math.round(p.baseline * 100) / 100}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Verdict row. */}
      <div className="flex flex-wrap items-center gap-2">
        {VERDICTS.map((v) => (
          <button
            key={v.value}
            type="button"
            disabled={verdictMutation.isPending}
            onClick={() =>
              verdictMutation.mutate({
                uuid: finding.uuid,
                verdict: v.value,
                ...(note.trim() ? { note: note.trim() } : {}),
              })
            }
            className="rounded border border-[var(--color-line)] bg-[var(--color-paper)] px-3 py-1 text-sm hover:bg-[var(--color-paper-alt)] disabled:opacity-50"
          >
            {v.label}
          </button>
        ))}
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="note (optional)"
          className="min-w-48 flex-1 rounded border border-[var(--color-line)] bg-[var(--color-paper)] px-2 py-1 text-sm"
        />
        {verdictMutation.isSuccess && (
          <span className="text-xs text-[var(--color-success,#0e9f6e)]">recorded</span>
        )}
      </div>

      {/* Release — the human filter. Nothing reaches a customer inbox
          until someone here decides it should. Deliberately separated
          from the verdict row: judging a finding and sending it are
          different decisions, and the destructive-ish one should not
          sit inside a row of one-click buttons. */}
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--color-line)] pt-3">
        {finding.released_at ? (
          <span className="text-sm text-[var(--color-success,#0e9f6e)]">
            Sent {fmtDay(finding.released_at)}
            {finding.released_by === 'system:auto' ? ' (automatically)' : ''}
          </span>
        ) : (
          <>
            <button
              type="button"
              disabled={releaseMutation.isPending}
              onClick={() =>
                releaseMutation.mutate({
                  uuid: finding.uuid,
                  ...(note.trim() ? { note: note.trim() } : {}),
                })
              }
              className="rounded bg-[var(--color-ink)] px-3 py-1.5 text-sm font-semibold text-[var(--color-paper)] hover:opacity-90 disabled:opacity-50"
            >
              {releaseMutation.isPending ? 'Sending…' : 'Release — email this'}
            </button>
            <span className="text-xs text-[var(--color-mute)]">
              Held. Emails the workspace's subscribed endpoints.
            </span>
          </>
        )}
        {releaseMutation.isError && (
          <span className="text-xs text-[var(--color-danger,#dc2626)]">
            {releaseMutation.error.message}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * What one manual pass did.
 *
 * The counters matter more here than a "done" toast: the agent's normal
 * failure is silence — a site visited, judged to have nothing, and
 * parked for a fortnight — and only the breakdown tells that apart from
 * a site that was genuinely examined.
 */
function RunSummary({ tick }: { tick: AdminAnalysisTick }): ReactElement {
  const visited = tick.sites_scanned + tick.sites_skipped_no_data + tick.sites_up_to_date;
  const parts = [
    `${visited} ${visited === 1 ? 'site' : 'sites'} visited`,
    `${tick.sites_scanned} analysed`,
    `${tick.sites_skipped_no_data} no data`,
  ];
  // Every outcome has to appear or the line silently fails to add up —
  // an all-`up_to_date` pass reads as "9 visited, nothing happened,
  // no reason given", which is how a no-op hides.
  if (tick.sites_up_to_date > 0) parts.push(`${tick.sites_up_to_date} already covered`);
  parts.push(`${tick.observations_written} observations`, `${tick.findings_created} new findings`);
  if (tick.findings_held > 0) parts.push(`${tick.findings_held} held for review`);
  if (tick.findings_resolved > 0) parts.push(`${tick.findings_resolved} auto-resolved`);

  const trouble: string[] = [];
  if (tick.errors > 0) trouble.push(`${tick.errors} errored`);
  if (tick.sites_rate_limited > 0) trouble.push(`${tick.sites_rate_limited} rate-limited`);
  if (tick.circuit_tripped) trouble.push('circuit tripped — the rest of the batch was dropped');

  return (
    <div className="mt-2 text-sm">
      <span className="text-[var(--color-ink-soft)]">{parts.join(' · ')}</span>
      <span className="text-[var(--color-mute)]">
        {` · ${tick.pipe_calls} pipe calls · ${(tick.duration_ms / 1000).toFixed(1)}s`}
      </span>
      {trouble.length > 0 && (
        <div className="mt-1 text-[var(--color-danger,#dc2626)]">{trouble.join(' · ')}</div>
      )}
    </div>
  );
}

/**
 * The agent's working-out, under the run controls.
 *
 * Collapsed by default — findings are the job, observations are the
 * proof of life. Open it when the queue is empty and you need to know
 * which of the two empties you are looking at.
 */
function ObservationsPanel({ enabled }: { enabled: boolean }): ReactElement {
  const [open, setOpen] = useState(false);
  const observations = useAdminAnalysisObservations(enabled && open);
  const rows = observations.data?.observations ?? [];

  return (
    <div className="mb-4 rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold">Recent observations</span>
        <span className="text-xs text-[var(--color-mute)]">
          {open ? 'Hide' : 'What the detector recorded, finding or not'}
        </span>
      </button>
      {open && (
        <div className="border-t border-[var(--color-line)] px-4 py-3">
          {observations.isPending && <p className="text-sm text-[var(--color-mute)]">Loading…</p>}
          {observations.isError && (
            <p className="text-sm text-[var(--color-danger,#dc2626)]">
              {observations.error.message}
            </p>
          )}
          {observations.isSuccess && rows.length === 0 && (
            <p className="text-sm text-[var(--color-mute)]">
              Nothing recorded yet. A pass that writes no observations either found every series
              steady, or never reached its data.
            </p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--color-mute)]">
                    <th className="py-1 pr-3 font-medium">Site</th>
                    <th className="py-1 pr-3 font-medium">Day</th>
                    <th className="py-1 pr-3 font-medium">Detector</th>
                    <th className="py-1 pr-3 font-medium">Scope</th>
                    <th className="py-1 pr-3 font-medium">Metric</th>
                    <th className="py-1 pr-3 text-right font-medium">Value</th>
                    <th className="py-1 pr-3 text-right font-medium">Baseline</th>
                    <th className="py-1 pr-3 text-right font-medium">σ</th>
                    <th className="py-1 text-right font-medium">Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr
                      key={`${o.site_uuid}-${o.detector}-${o.scope}-${o.metric}-${o.observed_on}`}
                      className="border-t border-[var(--color-line)]"
                    >
                      <td className="py-1.5 pr-3">{o.site_label}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDay(o.observed_on)}</td>
                      <td className="py-1.5 pr-3 text-[var(--color-ink-soft)]">{o.detector}</td>
                      <td className="py-1.5 pr-3 text-[var(--color-ink-soft)]">{o.scope}</td>
                      <td className="py-1.5 pr-3">{o.metric}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {Math.round(o.value * 100) / 100}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-[var(--color-mute)]">
                        {Math.round(o.baseline * 100) / 100}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {o.deviation.toFixed(1)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-[var(--color-mute)]">
                        {o.sample_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-xs text-[var(--color-mute)]">
            Third-party roster rows are hidden — they record every entity present, not anything that
            moved.
          </p>
        </div>
      )}
    </div>
  );
}

function AdminAnalysisPage(): ReactElement {
  const { isSystemAdmin } = useIsSystemAdmin();
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const stats = useAdminAnalysisStats(isSystemAdmin);
  const findings = useAdminAnalysisFindings(isSystemAdmin, status, kind);
  const rows = findings.data?.pages.flatMap((p) => p.findings) ?? [];
  const runMutation = useRunAnalysisMutation();

  return (
    <Island title="Ambient analysis">
      <p className="mb-4 text-sm text-[var(--color-mute)]">
        Machine findings, human verdicts. good/(good+bad) is the go/no-go number.
      </p>

      {/* Manual trigger. The agent visits a site roughly weekly and
          defers a fruitless visit by a fortnight, so without this there
          is no way to look at a site on demand — or to see why a pass
          found nothing. */}
      <div className="mb-4 rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={runMutation.isPending}
            onClick={() => runMutation.mutate()}
            className="rounded bg-[var(--color-ink)] px-3 py-1.5 text-sm font-semibold text-[var(--color-paper)] hover:opacity-90 disabled:opacity-50"
          >
            {runMutation.isPending ? 'Analysing…' : 'Analyse now'}
          </button>
          <span className="text-xs text-[var(--color-mute)]">
            Visits every site now, ignoring the usual weekly schedule. Findings are still held for
            review — this never emails anyone.
          </span>
        </div>
        {runMutation.isError && (
          <div className="mt-2 text-sm text-[var(--color-danger,#dc2626)]">
            {runMutation.error.message}
          </div>
        )}
        {runMutation.isSuccess && <RunSummary tick={runMutation.data.tick} />}
      </div>

      <ObservationsPanel enabled={isSystemAdmin} />

      {/* Stats strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Precision"
          value={
            stats.data?.precision === null || stats.data?.precision === undefined
              ? '—'
              : `${Math.round(stats.data.precision * 100)}%`
          }
        />
        <StatCard label="Open" value={String(stats.data?.totals.open ?? '—')} />
        <StatCard label="Resolved (30d)" value={String(stats.data?.totals.resolved ?? '—')} />
        <StatCard
          label="Verdicts"
          value={String(
            stats.data ? Object.values(stats.data.verdicts).reduce((a, b) => a + b, 0) : '—',
          )}
        />
      </div>

      {/* Filters */}
      <div className="mb-3 flex gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border border-[var(--color-line)] bg-[var(--color-paper)] px-2 py-1 text-sm"
        >
          <option value="open">open</option>
          <option value="resolved">resolved</option>
          <option value="dismissed">dismissed</option>
          <option value="">all statuses</option>
        </select>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded border border-[var(--color-line)] bg-[var(--color-paper)] px-2 py-1 text-sm"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k === '' ? 'all kinds' : k.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {/* Findings table */}
      <div className="overflow-x-auto rounded-lg border border-[var(--color-line)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-line)] text-left text-[11px] uppercase tracking-wide text-[var(--color-mute)]">
              <th className="px-3 py-2">Site</th>
              <th className="px-3 py-2">Finding</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2 text-right">Proof</th>
              <th className="px-3 py-2 text-right">Priority</th>
              <th className="px-3 py-2 text-right">Seen</th>
              <th className="px-3 py-2">Delivery</th>
              <th className="px-3 py-2">Verdicts</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-[var(--color-mute)]">
                  {findings.isLoading
                    ? 'Loading…'
                    : 'No findings. For this feature, silence is the healthy state.'}
                </td>
              </tr>
            )}
            {rows.map((f) => (
              <Fragment key={f.uuid}>
                {/* The whole row toggles the evidence panel. Keyboard
                    parity matters here specifically: reviewing findings
                    is a keyboard-heavy task (read, judge, next), so the
                    row is focusable and responds to Enter/Space rather
                    than being mouse-only. */}
                <tr
                  onClick={() => setExpanded(expanded === f.uuid ? null : f.uuid)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpanded(expanded === f.uuid ? null : f.uuid);
                    }
                  }}
                  tabIndex={0}
                  aria-expanded={expanded === f.uuid}
                  className="cursor-pointer border-b border-[var(--color-line)] last:border-b-0 hover:bg-[var(--color-paper-alt)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-accent,#2463e6)]"
                >
                  <td className="px-3 py-2 whitespace-nowrap">{f.site_label}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <DirectionBadge direction={f.direction} />
                      <span>{f.title}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[var(--color-mute)]">
                    {f.kind.replace(/_/g, ' ')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{f.proof_score}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{f.priority}</td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    ×{f.occurrence_count} · {fmtDay(f.window_end)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {f.released_at ? (
                      <span className="text-xs text-[var(--color-success,#0e9f6e)]">sent</span>
                    ) : (
                      <span className="text-xs text-[var(--color-mute)]">held</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <VerdictChips finding={f} />
                  </td>
                </tr>
                {expanded === f.uuid && (
                  <tr>
                    <td colSpan={8} className="p-0">
                      <FindingDetail uuid={f.uuid} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {findings.hasNextPage && (
        <button
          type="button"
          onClick={() => findings.fetchNextPage()}
          disabled={findings.isFetchingNextPage}
          className="mt-3 rounded border border-[var(--color-line)] px-3 py-1.5 text-sm hover:bg-[var(--color-paper-alt)] disabled:opacity-50"
        >
          {findings.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}
    </Island>
  );
}
